import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Check } from 'lucide-react';
import { Product } from '../data/products';
import { useCart } from '../context/CartContext';
interface ProductCardProps {
  product: Product;
  compact?: boolean;
  bestForLabel?: string;
}
export function ProductCard({
  product,
  compact = false,
  bestForLabel
}: ProductCardProps) {
  const {
    addToCart
  } = useCart();
  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
  };
  return <motion.div whileHover={{
    y: -4
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 25
  }} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl hover:border-gray-300 transition-all duration-300 flex flex-col h-full group" data-id="element-178">
      <Link to={`/product/${product.slug}`} className="block relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden p-4 sm:p-6" data-id="element-179">
        <img src={product.image} alt={`${product.name} - Yarbo robotic yard-care system`} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" loading="lazy" data-id="element-180" />
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-green-50 text-green-700 ring-1 ring-inset ring-green-200 text-[11px] font-bold rounded-full px-2 py-1" data-id="element-181">
          <Check className="w-3 h-3" data-id="element-182" /> In Stock
        </div>
        {product.isPopular && <div className="absolute top-3 right-3 bg-yarbo-yellow text-yarbo-dark text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide" data-id="element-183">
            Popular
          </div>}
      </Link>

      <div className="p-5 flex flex-col flex-grow" data-id="element-184">
        {product.requiresCore && <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-2" data-id="element-185">
            Module Only · Requires Yarbo Core
          </span>}
        {bestForLabel && <span className="text-[10px] font-bold uppercase tracking-wider text-yarbo-dark/70 mb-2" data-id="element-186">
            Best for: {bestForLabel}
          </span>}

        <h3 className="text-base sm:text-lg font-bold text-yarbo-dark leading-tight mb-1 line-clamp-2" data-id="element-187">
          <Link to={`/product/${product.slug}`} className="hover:text-black transition-colors" data-id="element-188">
            {product.name}
          </Link>
        </h3>

        <div className="text-xl sm:text-2xl font-extrabold text-yarbo-dark mb-3" data-id="element-189">
          ${product.price.toLocaleString()}
        </div>

        {!compact && <ul className="space-y-1.5 mb-4 text-xs text-gray-700 flex-grow" data-id="element-190">
            {product.features.slice(0, 3).map((f, idx) => <li key={idx} className="flex items-start gap-1.5 line-clamp-1" data-id="element-191">
                <Check className="w-3 h-3 text-yarbo-green mt-0.5 flex-shrink-0" data-id="element-192" />
                <span data-id="element-193">{f}</span>
              </li>)}
          </ul>}

        <div className="mt-auto flex flex-col gap-2" data-id="element-194">
          <button onClick={handleAdd} className="w-full bg-yarbo-dark text-white font-bold text-sm py-3 rounded-lg hover:bg-black transition-colors flex items-center justify-center gap-2" data-id="element-195">
            <ShoppingCart className="w-4 h-4" data-id="element-196" /> Add to Cart
          </button>
          <Link to={`/product/${product.slug}`} className="w-full text-center text-sm font-semibold text-gray-700 hover:text-yarbo-dark py-1" data-id="element-197">
            View Details →
          </Link>
        </div>
      </div>
    </motion.div>;
}